import { useTranslation } from 'react-i18next';

import * as St from './WhatToExpectSection.styles';

import PunchHolesGroup from '~/UI/components/atoms/PunchHolesGroup';
import { useIsLaminateProductOnlySelected } from '~/api/easy-estimator-api/materials-api/hooks/useIsLaminateProductOnlySelected';
import { useRetailerDocument } from '~/core/hooks/useRetailerDocuments';
import { RetailerDocumentType } from '~/core/models';
import DeliveryIcon from '~assets/icons/Delivery Icon.svg';
import FabricationIcon from '~assets/icons/Fabrication Icon.svg';
import InstallIcon from '~assets/icons/Install Icon.svg';
import PurchasingIcon from '~assets/icons/Purchasing Icon.svg';
import SchedulingIcon from '~assets/icons/Scheduling Icon.svg';
import VerificationIcon from '~assets/icons/Verification Icon.svg';
import PageTitle from '~atoms/PageTitle';
import PunchHoleItem from '~atoms/PunchHoleItem';

const WhatToExpectSection = (): JSX.Element => {
  const { t } = useTranslation();
  const isLaminateProductOnly = useIsLaminateProductOnlySelected();
  const templateChecklistURL = useRetailerDocument(
    RetailerDocumentType.TEMPLATE_CHECKLIST
  );
  const installationChecklistURL = useRetailerDocument(
    RetailerDocumentType.INSTALL_CHECKLIST
  );

  const purchaseStep = (
    <PunchHoleItem
      key="purchase"
      icon={<PurchasingIcon />}
      title={t('ORDER_SUMMARY.WHAT_TO_EXPECT.PURCHASE_TITLE')}
      content={t('ORDER_SUMMARY.WHAT_TO_EXPECT.PURCHASE_CONTENT')}
    />
  );

  const fabricationStep = (
    <PunchHoleItem
      key="fabrication"
      icon={<FabricationIcon />}
      title={t('ORDER_SUMMARY.WHAT_TO_EXPECT.FABRICATION_TITLE')}
      content={t(
        isLaminateProductOnly
          ? 'ORDER_SUMMARY.WHAT_TO_EXPECT.FABRICATION_CONTENT_PRODUCT_ONLY'
          : 'ORDER_SUMMARY.WHAT_TO_EXPECT.FABRICATION_CONTENT'
      )}
    />
  );

  const steps = isLaminateProductOnly
    ? [
        purchaseStep,
        fabricationStep,
        <PunchHoleItem
          key="delivery"
          icon={<DeliveryIcon />}
          title={t('ORDER_SUMMARY.WHAT_TO_EXPECT.DELIVERY_TITLE')}
          content={t('ORDER_SUMMARY.WHAT_TO_EXPECT.DELIVERY_CONTENT')}
        />,
      ]
    : [
        purchaseStep,
        <PunchHoleItem
          key="scheduling"
          icon={<SchedulingIcon />}
          title={t('ORDER_SUMMARY.WHAT_TO_EXPECT.SCHEDULING_TITLE')}
          content={t('ORDER_SUMMARY.WHAT_TO_EXPECT.SCHEDULING_CONTENT')}
          link={
            templateChecklistURL
              ? {
                  href: templateChecklistURL,
                  text: t(
                    'ORDER_SUMMARY.WHAT_TO_EXPECT.SCHEDULING_CONTENT_CLICKABLE'
                  ),
                }
              : undefined
          }
        />,
        <PunchHoleItem
          key="verification"
          icon={<VerificationIcon />}
          title={t('ORDER_SUMMARY.WHAT_TO_EXPECT.VERIFICATION_TITLE')}
          content={t('ORDER_SUMMARY.WHAT_TO_EXPECT.VERIFICATION_CONTENT')}
        />,
        fabricationStep,
        <PunchHoleItem
          key="install"
          icon={<InstallIcon />}
          title={t('ORDER_SUMMARY.WHAT_TO_EXPECT.INSTALL_TITLE')}
          content={t('ORDER_SUMMARY.WHAT_TO_EXPECT.INSTALL_CONTENT')}
          link={
            installationChecklistURL
              ? {
                  href: installationChecklistURL,
                  text: t(
                    'ORDER_SUMMARY.WHAT_TO_EXPECT.INSTALL_CONTENT_CLICKABLE'
                  ),
                }
              : undefined
          }
        />,
      ];

  return (
    <St.Container>
      <PageTitle>{t('ORDER_SUMMARY.WHAT_TO_EXPECT.TITLE')}</PageTitle>
      <PunchHolesGroup>{steps}</PunchHolesGroup>
    </St.Container>
  );
};

export default WhatToExpectSection;
